import { keywordTopic } from './match';
import { SLM, TOPIC_MODEL } from './models';
import type { FromUnderstand, Part, ToUnderstand } from './protocol';
import { TOPIC_QUESTION } from './slm';
import { NO_TOPIC, TOPICS, type Topic } from './topics';

/** Below this probability the language model's topic is not trusted; the keywords decide. */
export const TOPIC_TRUST = 0.5;

interface Pending {
  resolve: (value: FromUnderstand) => void;
  reject: (error: Error) => void;
}

/** The answer to a question, and how it was found (for the debug page and tests). */
export interface Answer {
  topic: Topic | null;
  text: string;
  by: 'model' | 'keywords' | 'none';
  p?: number;
}

/**
 * The app's side of the understanding worker: what the child said (speech-to-text) and which
 * approved topic a question is about (the small language model, with the keyword fallback while
 * it loads, when it fails, or when it is unsure). Nothing loads until first used.
 */
export class Understanding {
  private worker: Worker | undefined;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();
  private readonly loads = new Map<Part, Promise<void>>();
  private readonly loadSettled = new Map<
    Part,
    { resolve: () => void; reject: (e: Error) => void }
  >();
  private readonly ready = new Set<Part>();
  private readonly progress = new Set<(part: Part, loaded: number, total: number) => void>();

  constructor(onProgress?: (part: Part, loaded: number, total: number) => void) {
    if (onProgress) this.progress.add(onProgress);
  }

  /** Download progress of each part; returns the unsubscribe. */
  onProgress(listener: (part: Part, loaded: number, total: number) => void): () => void {
    this.progress.add(listener);
    return () => {
      this.progress.delete(listener);
    };
  }

  private ensure(): Worker {
    if (!this.worker) {
      const worker = new Worker(new URL('./understand.worker.ts', import.meta.url), {
        type: 'module',
        name: 'understanding',
      });
      worker.onmessage = (event: MessageEvent<FromUnderstand>) => {
        this.handle(event.data);
      };
      worker.onerror = (event) => {
        const error = new Error(event.message || 'understanding worker failed');
        for (const p of this.pending.values()) p.reject(error);
        this.pending.clear();
        for (const s of this.loadSettled.values()) s.reject(error);
        this.loadSettled.clear();
      };
      this.worker = worker;
    }
    return this.worker;
  }

  private handle(message: FromUnderstand) {
    if (message.type === 'progress') {
      for (const listener of this.progress) listener(message.part, message.loaded, message.total);
      return;
    }
    if (message.type === 'ready') {
      this.ready.add(message.part);
      this.loadSettled.get(message.part)?.resolve();
      this.loadSettled.delete(message.part);
      return;
    }
    if (message.type === 'error' && message.id === undefined) {
      if (message.part) {
        this.loadSettled.get(message.part)?.reject(new Error(message.message));
        this.loadSettled.delete(message.part);
        this.loads.delete(message.part); // a later use may retry
      }
      return;
    }
    const id = message.id;
    const pending = id === undefined ? undefined : this.pending.get(id);
    if (id === undefined || !pending) return;
    this.pending.delete(id);
    if (message.type === 'error') pending.reject(new Error(message.message));
    else pending.resolve(message);
  }

  private request(message: ToUnderstand): Promise<FromUnderstand> {
    const id = message.type === 'load' ? 0 : message.id;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.ensure().postMessage(message);
    });
  }

  /** Starts loading a part (speech-to-text or the language model); resolves when it is ready. */
  load(part: Part): Promise<void> {
    let loading = this.loads.get(part);
    if (!loading) {
      loading = new Promise<void>((resolve, reject) => {
        this.loadSettled.set(part, { resolve, reject });
        this.ensure().postMessage({ type: 'load', part } satisfies ToUnderstand);
      });
      this.loads.set(part, loading);
    }
    return loading;
  }

  isReady(part: Part): boolean {
    return this.ready.has(part);
  }

  /** What the child said in a 16 kHz utterance. */
  async transcribe(audio: Float32Array): Promise<{ text: string; ms: number }> {
    const reply = await this.request({ type: 'transcribe', id: this.nextId++, audio });
    if (reply.type !== 'text') throw new Error('unexpected reply');
    return { text: reply.text, ms: reply.ms };
  }

  /**
   * The approved answer to a question. The keywords first; when they find nothing, the topic
   * model if it is loaded and sure (p >= TOPIC_MODEL.trust); otherwise the "don't know" answer.
   * (The small language model is the older route, off by default: SLM.use.)
   */
  async answer(text: string): Promise<Answer> {
    const keyword = keywordTopic(text);
    const fallback = (): Answer =>
      keyword
        ? { topic: keyword.topic, text: keyword.topic.answer, by: 'keywords' }
        : { topic: null, text: NO_TOPIC, by: 'none' };
    if (TOPIC_MODEL.use) return keyword ? fallback() : this.byTopicModel(text, fallback);
    if (!SLM.use) return fallback();
    void this.load('slm').catch(() => undefined);
    if (!this.isReady('slm')) return fallback();
    try {
      const reply = await this.choose(
        TOPIC_QUESTION,
        TOPICS.map((t) => t.about),
        text,
      );
      if (reply.p < TOPIC_TRUST) return fallback();
      const topic = reply.index === null ? null : (TOPICS[reply.index] ?? null);
      return topic
        ? { topic, text: topic.answer, by: 'model', p: reply.p }
        : { topic: null, text: NO_TOPIC, by: 'model', p: reply.p };
    } catch {
      return fallback();
    }
  }

  private async byTopicModel(text: string, fallback: () => Answer): Promise<Answer> {
    void this.load('topic').catch(() => undefined);
    if (!this.isReady('topic')) return fallback();
    try {
      const reply = await this.rank(
        TOPIC_QUESTION,
        TOPICS.map((t) => t.about),
        text,
      );
      if (reply.p < TOPIC_MODEL.trust) return fallback();
      const topic = reply.index === null ? null : (TOPICS[reply.index] ?? null);
      return topic
        ? { topic, text: topic.answer, by: 'model', p: reply.p }
        : { topic: null, text: NO_TOPIC, by: 'model', p: reply.p };
    } catch {
      return fallback();
    }
  }

  /**
   * Which of `options` (labels) the child meant by `text` in answer to `question`, from the
   * cross-encoder (null: none of them; `p`: how sure). Loads it on first use.
   */
  async rank(
    question: string,
    options: readonly string[],
    text: string,
  ): Promise<{ index: number | null; p: number }> {
    void this.load('topic').catch(() => undefined);
    const reply = await this.request({
      type: 'rank',
      id: this.nextId++,
      question,
      options: [...options],
      text,
    });
    if (reply.type !== 'choice') throw new Error('unexpected reply');
    return { index: reply.index, p: reply.p };
  }

  /**
   * Which of `options` the child meant by `text` in answer to `question` (null: none of them),
   * from the small language model; `p` is how sure it is. Loads the model on first use.
   */
  async choose(
    question: string,
    options: readonly string[],
    text: string,
  ): Promise<{ index: number | null; p: number }> {
    const reply = await this.request({
      type: 'choose',
      id: this.nextId++,
      question,
      options: [...options],
      text,
    });
    if (reply.type !== 'choice') throw new Error('unexpected reply');
    return { index: reply.index, p: reply.p };
  }

  /** Whether the cross-encoder is loaded (it answers once it is; until then, the rules do). */
  get canRank(): boolean {
    return TOPIC_MODEL.use && this.isReady('topic');
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = undefined;
    const error = new Error('understanding disposed');
    for (const p of this.pending.values()) p.reject(error);
    this.pending.clear();
  }
}

let shared: Understanding | undefined;

/** The one Understanding the whole game shares, so the models load once. */
export function sharedUnderstanding(): Understanding {
  shared ??= new Understanding();
  return shared;
}
